import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';

import type { PermissionsMap } from './permissionModules';
import type { AppRole } from './useUsers';

export interface PermissionProfile {
  id: string;
  nome: string;
  descricao: string | null;
  permissoes: PermissionsMap;
  ativo: boolean;
  created_at: string;
  updated_at: string;
  user_count?: number;
}

const QUERY_KEY = ['permission-profiles'];

/**
 * Mensagem mostrada no lugar de uma gravação real (CLAUDE.md, "Regras de
 * interface", item 8). Usada pelas três mutações abaixo: não existe módulo de
 * perfis de permissão customizados em `apps/api/src/modulos/` (confirmado:
 * nenhuma rota para `permission_profiles`).
 */
const AVISO_DE_ACAO_INDISPONIVEL =
  'Estamos preparando esta funcionalidade. Em breve você poderá salvar por aqui.';

function timestampNoPassado(dias: number): string {
  return new Date(Date.now() - dias * 86_400_000).toISOString();
}

/**
 * TEMPORÁRIO — mock local, remover no desenvolvimento do backend. Não existe
 * módulo de perfis de permissão customizados em `apps/api/src/modulos/` nem
 * rota que conte usuários por papel do sistema — decisão do dono de
 * 2026-08-23, registrada em CLAUDE.md, seção "Proibido". Somem inteiros
 * quando as rotas existirem, e `usePermissionProfiles`/`usePermissionProfile`/
 * `useSystemRoleUserCounts` voltam a chamar `chamarApi`.
 */
const PERFIS_MOCK: PermissionProfile[] = [
  {
    id: 'perfil-exemplo-fiscalizacao-01',
    nome: 'Fiscalização externa',
    descricao: 'Consulta a ordens de serviço e apontamentos, sem edição de cadastro.',
    permissoes: {},
    ativo: true,
    created_at: timestampNoPassado(120),
    updated_at: timestampNoPassado(20),
    user_count: 2,
  },
  {
    id: 'perfil-exemplo-suporte-02',
    nome: 'Suporte administrativo',
    descricao: 'Acesso de leitura a credenciados e convites, sem permissão financeira.',
    permissoes: {},
    ativo: true,
    created_at: timestampNoPassado(60),
    updated_at: timestampNoPassado(5),
    user_count: 0,
  },
  {
    id: 'perfil-exemplo-descontinuado-03',
    nome: 'Perfil descontinuado',
    descricao: null,
    permissoes: {},
    ativo: false,
    created_at: timestampNoPassado(300),
    updated_at: timestampNoPassado(200),
    user_count: 0,
  },
];

/**
 * TEMPORÁRIO — mock local, remover no desenvolvimento do backend. Ver
 * comentário acima: sem rota de contagem por papel do sistema.
 */
const CONTAGEM_POR_PAPEL_MOCK: Partial<Record<AppRole, number>> = {
  ADMIN: 1,
  FINANCEIRO: 1,
  GESTOR: 1,
  FISCAL: 1,
  OPERADOR_SME: 1,
  GESTOR_DRE: 1,
};

export function useSystemRoleUserCounts() {
  return useQuery({
    queryKey: ['system-role-user-counts'],
    // TEMPORÁRIO — mock local, remover no desenvolvimento do backend.
    queryFn: async (): Promise<Record<AppRole, number>> =>
      CONTAGEM_POR_PAPEL_MOCK as Record<AppRole, number>,
  });
}

export function usePermissionProfiles() {
  return useQuery({
    queryKey: QUERY_KEY,
    // TEMPORÁRIO — mock local, remover no desenvolvimento do backend.
    queryFn: async (): Promise<PermissionProfile[]> => PERFIS_MOCK,
  });
}

export function usePermissionProfile(id: string | undefined) {
  return useQuery({
    queryKey: [...QUERY_KEY, id],
    enabled: !!id,
    // TEMPORÁRIO — mock local, remover no desenvolvimento do backend.
    queryFn: async (): Promise<PermissionProfile> => {
      const perfil = PERFIS_MOCK.find((p) => p.id === id);
      if (!perfil) throw new Error('Perfil não encontrado.');
      return perfil;
    },
  });
}

/** Não grava: sem rota de escrita para perfis de permissão customizados. */
export function useCreatePermissionProfile() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (_profile: {
      nome: string;
      descricao?: string;
      permissoes: PermissionsMap;
      ativo?: boolean;
    }): Promise<void> => {
      throw new Error(AVISO_DE_ACAO_INDISPONIVEL);
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: QUERY_KEY }),
  });
}

export function useUpdatePermissionProfile() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (_profile: {
      id: string;
      nome: string;
      descricao?: string;
      permissoes: PermissionsMap;
      ativo?: boolean;
    }): Promise<void> => {
      throw new Error(AVISO_DE_ACAO_INDISPONIVEL);
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: QUERY_KEY }),
  });
}

export function useDeletePermissionProfile() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (_id: string): Promise<void> => {
      throw new Error(AVISO_DE_ACAO_INDISPONIVEL);
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: QUERY_KEY }),
  });
}
